import {Injectable} from '@angular/core';
import {AlertService} from "./alert.service";
import {LogService} from './log.service';
import {from, Observable, of} from 'rxjs';
import {StateService} from './state.service';
// import {OrderService, Product} from './order.service';
import {map, mergeMap, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {ComponentType} from '@angular/cdk/portal/portal';

const TEST_KIT_SKU: string = 'covid19_test_kit';

export interface ErrorTranslation {
    pattern: RegExp;
    message: string;
    target?: any;
}

@Injectable({
    providedIn: 'root'
})
export class UtilService {

    constructor(private alertService: AlertService,
                private state: StateService,
                // private orderService: OrderService,
                private router: Router,
                private dialog: MatDialog,
                private logger: LogService) {
    }

    /** Generates and returns an error handler, i.e., a function that can receive an error produced by an observable
     * and show an alert about it to the user.
     *
     * @param defaultMessage (optional) a message string to use if the function can't find the message text in the error object it receives;
     *              if not provided, the default message is "Error"
     * @param mapper (optional) a translation map relating regular expressions to translated messages; used to translate well-defined
     *              but user-unfriendly messages generated by services, into user-friendly messages, if matched
     */
    public getErrorHandler(defaultMessage?: string, mapper?: ErrorTranslation[]): (err:any) => void {
        return (error:any) => {
            this.logger.log("Error Handler:", error);
            if (error && error.error instanceof Blob)  {
                error.error.text().then( text => {
                    const info = JSON.parse(text);
                    const message = info.message || defaultMessage || "Error";
                    this.alertService.error(this.translateMessage(message, mapper));
                });
            } else {
                const message = error && (error.error && error.error.message || error.message) || defaultMessage || "Error";
                this.alertService.error(this.translateMessage(message, mapper));
            }
        }
    }

    public getErrorHandlerWithRedir(defaultMessage?: string, mapper?: ErrorTranslation[], defaultTarget?: string): (err:any) => void {
        return (error:any) => {
            this.logger.log("Error Handler:", error);
            if (error && error.error instanceof Blob)  {
                let message, target;
                from(error.error.text()).pipe(
                    mergeMap((text:string) => {
                        const info = JSON.parse(text);
                        const origMessage = info.message || defaultMessage || "Error";
                        [message, target] = this.translateMessageTarget(origMessage, mapper)
                        target = target || defaultTarget;
                        return this.alertService.errorObs(message);
                    })
                ).subscribe( () => {
                    // Once the alert is closed, it should navigate to the target URL
                    if (target)
                        this.router.navigateByUrl(target).then();
                });
            } else {
                let message, target;
                const origMessage = error && (error.error && error.error.message || error.message) || defaultMessage || "Error";
                [message, target] = this.translateMessageTarget(origMessage, mapper)
                target = target || defaultTarget;
                // Once the alert is closed, it should navigate to the target URL
                this.alertService.errorObs(message).subscribe(() => {
                    if (target)
                        this.router.navigateByUrl(target).then();
                });
            }
        }
    }

    /** Checks if the given message matches any of the translation patterns provided, and if so returns the translated message.
     * Otherwise it returns the original message unchanged.
     *
     * @param message
     * @param mapper
     */
    private translateMessage(message: string, mapper: ErrorTranslation[]): string {
        if (mapper) {
            const trans: ErrorTranslation = mapper.find((t) => message.match(t.pattern));
            if (trans)
                message = trans.message;
        }
        return message;
    }

    private translateMessageTarget(message: string, mapper: ErrorTranslation[]): any[] {
        let target;
        if (mapper) {
            const trans: ErrorTranslation = mapper.find((t) => message.match(t.pattern));
            if (trans) {
                message = trans.message;
                target = trans.target;
            }
        }
        return [message, target];
    }

    /** Adds/removes the "busy" class to the document body. Intended to show user some feedback about busy state, relying on some
     * global style.
     *
     * @param flag
     */
    public setBodyBusy(flag: boolean) {
        if (flag)
            document.body.classList.add("busy");
        else
            document.body.classList.remove("busy");
    }

    /** Given a state service key string, checks if the value is present in the state and if so, it returns an immediate of() observable
     * of that value. Otherwise, it returns the given observable to retrieve the value asynchronously AND stores the value in state once
     * received.
     *
     * @param key {string} the key where this value should be stored in state
     * @param obs {Observable<T>} observable to subscribe to in order to get the value when not stored in state
     * @return {Observable<T>} the proper observable to obtain this value regardless of source
     */
    public getStateObservable<T>(key: string, obs: Observable<T>): Observable<T> {
        const value:T = this.state.get(key);
        return value? of(value) : obs.pipe(tap((value:T) => { this.state.set(key, value) }));
    }

    /** Convenience method to make it easier to open a dialog using any component designed to act as contents of a MatDialog.
     * Dlg generic type: the dialog component class
     * Ret generic type: the type of the value returned by the observable as the result of the dialog on non-cancel close
     * Data generic type: the type of the data member of the config object passed as second parameter here
     *
     * @param dialogComponent pass the dialog component class here- -- same as the Dlg generic parameter
     * @param config MatDialog configuration options, including optional data attribute of Data type
     * @return Observable with the returned result, of Ret type
     */
    public openDialog<Dlg, Ret=any, Data=any>(dialogComponent: ComponentType<Dlg>, config: MatDialogConfig<Data>): Observable<Ret> {
        const ref: MatDialogRef<Dlg, Ret> = this.dialog.open(dialogComponent, config);
        return ref.afterClosed();
    }

    /** Checks if state represents a currently logged in patient user
     */
    isLoggedIn(): boolean {
        return this.state.get('authToken') && ['super', 'admin'].includes(this.state.get('userType'));
    }
}
